import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  MapPin,
  Plus,
  Edit,
  User,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/app/components/ui/dialog';
import {
  useClient,
  useClientContacts,
  useCreateClientContact,
  useUpdateClient,
  useProjects,
  useInvoices,
  usePortalAccesses,
  useCreatePortalAccess,
  useDeletePortalAccess,
  useRegeneratePortalToken,
} from '@/hooks/use-projects';
import { useOrgCurrency, useOrgTimeZone } from '@/hooks/use-settings';
import { formatMoney, OPTIONAL_FRACTION_DIGITS } from '@/lib/currency';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';

export function ClientDetailPage() {
  const currency = useOrgCurrency();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const clientId = parseInt(id ?? '0');

  const { data: client, isLoading } = useClient(clientId);
  const { data: contacts } = useClientContacts(clientId);
  const { data: projects } = useProjects({ client: clientId, page_size: 50 });
  const { data: invoices } = useInvoices({ client: clientId, page_size: 50 });
  const updateClient = useUpdateClient();
  const createContact = useCreateClientContact();

  const [isEditing, setIsEditing] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    title: '',
    email: '',
    phone: '',
    is_primary: false,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">Client not found</p>
      </div>
    );
  }

  const handleAddContact = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createContact.mutateAsync({ ...contactForm, client: clientId });
      setShowContactDialog(false);
      setContactForm({ name: '', title: '', email: '', phone: '', is_primary: false });
    } catch {
      // handled by mutation
    }
  };

  const handleToggleActive = async () => {
    await updateClient.mutateAsync({
      id: clientId,
      data: { is_active: !client.is_active },
    });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/clients')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Clients
        </Button>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="p-3 bg-blue-50 rounded-lg">
            <Building2 className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
            {client.contact_name && (
              <p className="text-gray-600">{client.contact_name}</p>
            )}
            <Badge
              className="mt-1"
              variant={client.is_active ? 'default' : 'secondary'}
            >
              {client.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleToggleActive}>
            {client.is_active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setIsEditing(!isEditing)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contact Info */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {client.contact_email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <a href={`mailto:${client.contact_email}`} className="text-blue-600 hover:underline">
                    {client.contact_email}
                  </a>
                </div>
              )}
              {client.contact_phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <a href={`tel:${client.contact_phone}`} className="text-blue-600 hover:underline">
                    {client.contact_phone}
                  </a>
                </div>
              )}
              {(client.address || client.city) && (
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div>
                    {client.address && <p>{client.address}</p>}
                    <p>
                      {[client.city, client.state_province].filter(Boolean).join(', ')}
                      {client.postal_code ? ` ${client.postal_code}` : ''}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Billing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Payment Terms</span>
                <span>{client.payment_terms_days} days</span>
              </div>
              {client.billing_email && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Billing Email</span>
                  <span>{client.billing_email}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {client.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{client.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Contacts & Projects */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contacts */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Contacts</CardTitle>
              <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Contact
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Contact</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddContact} className="space-y-4">
                    <div>
                      <Label>Name *</Label>
                      <Input
                        value={contactForm.name}
                        onChange={(e) =>
                          setContactForm((p) => ({ ...p, name: e.target.value }))
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={contactForm.title}
                        onChange={(e) =>
                          setContactForm((p) => ({ ...p, title: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={contactForm.email}
                        onChange={(e) =>
                          setContactForm((p) => ({ ...p, email: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={contactForm.phone}
                        onChange={(e) =>
                          setContactForm((p) => ({ ...p, phone: e.target.value }))
                        }
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is_primary"
                        checked={contactForm.is_primary}
                        onChange={(e) =>
                          setContactForm((p) => ({ ...p, is_primary: e.target.checked }))
                        }
                      />
                      <Label htmlFor="is_primary">Primary Contact</Label>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowContactDialog(false)}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" disabled={createContact.isPending}>
                        {createContact.isPending ? 'Adding...' : 'Add Contact'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {contacts && contacts.length > 0 ? (
                <div className="space-y-3">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white rounded-full">
                          <User className="h-4 w-4 text-gray-500" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {contact.name}
                            {contact.is_primary && (
                              <Badge className="ml-2" variant="secondary">
                                Primary
                              </Badge>
                            )}
                          </p>
                          {contact.title && (
                            <p className="text-sm text-gray-500">{contact.title}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        {contact.email && (
                          <p className="text-blue-600">{contact.email}</p>
                        )}
                        {contact.phone && <p className="text-gray-500">{contact.phone}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No contacts added</p>
              )}
            </CardContent>
          </Card>

          {/* Projects */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Projects ({projects?.count ?? 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {projects && projects.results.length > 0 ? (
                <div className="space-y-2">
                  {projects.results.map((project) => (
                    <Link
                      key={project.id}
                      to={`/projects/${project.id}`}
                      className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <div>
                        <p className="font-medium">{project.name}</p>
                        <p className="text-sm text-gray-500">{project.project_number}</p>
                      </div>
                      <Badge>{project.status.replace('_', ' ')}</Badge>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No projects yet</p>
              )}
            </CardContent>
          </Card>

          {/* Portal Access */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Portal Access</CardTitle>
              <PortalAccessActions clientId={clientId} />
            </CardHeader>
            <CardContent>
              <PortalAccessList clientId={clientId} />
            </CardContent>
          </Card>

          {/* Billing History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Invoices ({invoices?.count ?? 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {invoices && invoices.results.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-gray-500">
                        <th className="pb-2 font-medium">Invoice #</th>
                        <th className="pb-2 font-medium">Project</th>
                        <th className="pb-2 font-medium">Amount</th>
                        <th className="pb-2 font-medium">Status</th>
                        <th className="pb-2 font-medium">Due Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {invoices.results.map((inv) => (
                        <tr key={inv.id} className="hover:bg-gray-50">
                          <td className="py-2 font-medium">{inv.invoice_number}</td>
                          <td className="py-2 text-gray-600">{inv.project_name}</td>
                          <td className="py-2">{formatMoney(inv.total, currency, OPTIONAL_FRACTION_DIGITS)}</td>
                          <td className="py-2">
                            <Badge variant="secondary">{inv.status}</Badge>
                          </td>
                          <td className="py-2 text-gray-600">{inv.due_date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No invoices yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function PortalAccessActions({ clientId }: { clientId: number }) {
  const createAccess = useCreatePortalAccess();
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await createAccess.mutateAsync({ client: clientId, email });
      setEmail('');
      setShowForm(false);
    } catch {
      // handled by mutation
    }
  };

  if (showForm) {
    return (
      <form onSubmit={handleCreate} className="flex gap-2 items-end">
        <div>
          <Label>Email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="client@example.com"
            required
            className="w-48"
          />
        </div>
        <Button type="submit" size="sm" disabled={createAccess.isPending}>
          {createAccess.isPending ? 'Creating...' : 'Generate Link'}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => setShowForm(false)}>
          Cancel
        </Button>
      </form>
    );
  }

  return (
    <Button size="sm" variant="outline" onClick={() => setShowForm(true)}>
      <Plus className="h-4 w-4 mr-1" />
      Generate Portal Link
    </Button>
  );
}

function PortalAccessList({ clientId }: { clientId: number }) {
  const timeZone = useOrgTimeZone();
  const { data: accesses } = usePortalAccesses({ client: clientId });
  const deleteAccess = useDeletePortalAccess();
  const regenerateToken = useRegeneratePortalToken();

  const results = accesses?.results ?? [];

  if (results.length === 0) {
    return <p className="text-sm text-gray-500 text-center py-4">No portal access configured</p>;
  }

  return (
    <div className="space-y-3">
      {results.map((access) => (
        <div
          key={access.id}
          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-full">
              <Mail className="h-4 w-4 text-gray-500" />
            </div>
            <div>
              <p className="font-medium text-gray-900">{access.email}</p>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Badge variant={access.is_active ? 'default' : 'secondary'}>
                  {access.is_active ? 'Active' : 'Inactive'}
                </Badge>
                {access.last_accessed && (
                  <span>
                    Last accessed:{' '}
                    {formatDateTimeInTimeZone(access.last_accessed, timeZone, {
                      dateStyle: 'medium',
                    })}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => regenerateToken.mutate(access.id)}
              disabled={regenerateToken.isPending}
              title="Regenerate token"
            >
              Regenerate
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (confirm('Remove this portal access?')) {
                  deleteAccess.mutate(access.id);
                }
              }}
              className="text-red-500 hover:text-red-700"
            >
              Remove
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
